import { useState, useEffect, useCallback } from 'react';
import { getJobCosts } from '../api/jobCosts';
import type { JobCost } from '../types/jobCost';

/**
 * Hook for fetching job costs
 */
export function useJobCosts(jobId: number | undefined, isForecast?: boolean) {
  const [data, setData] = useState<JobCost[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCosts = useCallback(async () => {
    if (!jobId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const costs = await getJobCosts(jobId, isForecast);
      setData(costs);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch job costs');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, isForecast]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCosts,
  };
}
